import * as React from 'react';
import { ChangeEvent } from 'react';
import { useState, useEffect } from 'react';
import {
	Snackbar,
	TextField,
	InputAdornment,
	Button,
	Stack,
	CircularProgress,
	Box,
} from '@mui/material';
import { connect } from 'react-redux';
import { ReduxState, RentalLinkData } from 'services/types';
import { setRentalLinkSendingFailed, setPendingRentalLinks } from 'actions';
import { OrderInfo, RentalLinkInfo } from 'common/types';
import countryCodes from 'common/services/countryCodes.json';
import moment from 'moment-timezone';
import { addNewSmsDeliveryStatusEvent } from 'common/api/frontend';
import { Routes, push } from 'routing';
import { useTranslation } from 'services/localization/useTranslation';
import { httpsCallable } from 'common/frontend/firebase/functions';
import { api } from 'common/frontend/api';
import Banner from './Banner';

interface StateToProps {
	rentalLinkSendingFailed: boolean;
	pendingRentalLinks: RentalLinkData[];
	activeBookedRentals: OrderInfo[];
}

interface DispatchProps {
	setRentalLinkSendingFailed: (failed: boolean) => void;
	setPendingRentalLinks: (pendingLinks: RentalLinkData[]) => void;
}

type InputField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

type Props = StateToProps & DispatchProps;

const SmsStatusSnackbar = (props: Props) => {
	const { t } = useTranslation();
	const [originalPhone, setOriginalPhone] = useState('');
	const [isOriginalPhoneSet, setIsOriginalPhoneSet] = useState(false);
	const [originalEmail, setOriginalEmail] = useState('');
	const [isOriginalEmailSet, setIsOriginalEmailSet] = useState(false);
	const [newLinkSent, setNewLinkSent] = useState(false);
	const [isSendingPaylink, setIsSendingPaylink] = useState(false);

	// TODO: Update email in rental object
	const handleEmailChange = (event: React.ChangeEvent<InputField>, rentalId: string) => {
		props.setPendingRentalLinks(
			props.pendingRentalLinks.map((link) =>
				link.rentalId === rentalId
					? {
							...link,
							email: event.target.value,
					  }
					: link,
			),
		);
	};

	// TODO: Update phone in rental object
	const handlePhoneChange = (event: ChangeEvent<InputField>, rentalId: string) => {
		props.setPendingRentalLinks(
			props.pendingRentalLinks.map((link) => {
				return link.rentalId === rentalId
					? {
							...link,
							phone: {
								...link.phone,
								numberWithoutCode: event.target.value,
							},
					  }
					: link;
			}),
		);
	};

	const handleAreaCodeChange = (event: ChangeEvent<InputField>, rentalId: string) => {
		const [countryCode, areaCode] = event.target.value.split(':');
		props.setPendingRentalLinks(
			props.pendingRentalLinks.map((link) => {
				return link.rentalId === rentalId
					? {
							...link,
							phone: {
								...link.phone,
								areaCode,
								countryCode,
							},
					  }
					: link;
			}),
		);
	};

	useEffect(() => {
		if (props.activeBookedRentals.length) {
			const updatedLinks = props.pendingRentalLinks.map((link) => {
				const status = getStatus(link.rentalId, props.activeBookedRentals);
				if (status === 'failed' && !newLinkSent && !isSendingPaylink) {
					props.setRentalLinkSendingFailed(true);
				} else if (status !== 'failed') {
					setNewLinkSent(false);
					props.setRentalLinkSendingFailed(false);
				}
				return {
					...link,
					status,
				};
			});
			props.setPendingRentalLinks(updatedLinks.filter((link) => link.status !== 'delivered'));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.activeBookedRentals]);

	useEffect(() => {
		if (
			props.pendingRentalLinks.length &&
			props.pendingRentalLinks.some((link) => link.status === 'failed')
		) {
			if (!isOriginalPhoneSet) {
				const phone = props.pendingRentalLinks.find((link) => link.status === 'failed')!.phone;
				setOriginalPhone(`${phone.areaCode}${phone.numberWithoutCode}`);
				setIsOriginalPhoneSet(true);
			}
			if (!isOriginalEmailSet) {
				const email = props.pendingRentalLinks.find((link) => link.status === 'failed')!.email;
				setOriginalEmail(email);
				setIsOriginalEmailSet(true);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.pendingRentalLinks]);

	const getStatus = (rentalId: string, rentals: OrderInfo[]): string => {
		const relevantRental = rentals.find((rental) => rental.id === rentalId);
		if (
			relevantRental &&
			relevantRental.rentalLink &&
			relevantRental.rentalLink.smsDeliveryStatusHistory &&
			relevantRental.rentalLink.smsDeliveryStatusHistory.length
		) {
			const statuses = relevantRental.rentalLink.smsDeliveryStatusHistory
				.sort((a, b) => moment(b.timestamp).diff(moment(a.timestamp))) // Sort by newest
				.map((e) => e.status);
			if (statuses.length && statuses[0] === 'delivered') {
				return 'delivered';
			} else if (statuses.length && statuses[0] === 'failed') {
				return 'failed';
			} else {
				return statuses[0] || '';
			}
		}
		return '';
	};

	const sendEmailPaylink = async (link: RentalLinkData) => {
		if (link.email) {
			const { shopPublicInfo, startLocation, payLinkUrl, language } = link;
			const emailPayLink = httpsCallable<any, any>('http-emailPayLink');
			const response = await emailPayLink({
				shop: shopPublicInfo,
				startLocation,
				to: link.email,
				payLinkUrl,
				language,
			});
			if (response.data && response.data.code === 'unknown') {
				return false;
			} else {
				return true;
			}
		}
		return false;
	};

	const sendSMSPaylink = async (link: RentalLinkData) => {
		if (link.phone.areaCode && link.phone.numberWithoutCode) {
			const newPhone = `${link.phone.areaCode}${link.phone.numberWithoutCode}`;
			const { shopPublicInfo, payLinkUrl, language, startLocation } = link;
			const smsPayLink = httpsCallable<any, any>('http-smsPayLink');
			const response = await smsPayLink({
				shop: shopPublicInfo,
				startLocation,
				to: newPhone,
				payLinkUrl,
				language,
				rentalId: link.rentalId,
			});
			if (response.data.code === 'unknown') {
				// Unknown indicates failed sending
				return false;
			} else {
				return true;
			}
		}
		return false;
	};

	const handleDeliveryStatusEventForEmail = async (
		link: RentalLinkData,
		isSMSSuccessful: boolean,
		isEmailSuccessful: boolean,
	) => {
		if (
			(!link.email && !isSMSSuccessful) || // if only sms was sent and did NOT arrive
			(!link.phone.numberWithoutCode && !isEmailSuccessful) || // if only email was sent and did NOT arrive
			(!isSMSSuccessful && !isEmailSuccessful) // if both were sent and did NOT arrive
		) {
			await addNewSmsDeliveryStatusEvent(link.rentalId, 'failed');
		} else if (isEmailSuccessful && !isSMSSuccessful) {
			// if sms did NOT arrive and email did
			await addNewSmsDeliveryStatusEvent(link.rentalId, 'sent');
			await addNewSmsDeliveryStatusEvent(link.rentalId, 'delivered');
			props.setRentalLinkSendingFailed(false);
		}
	};

	const handleResendPaylink = async (data: RentalLinkData) => {
		setIsSendingPaylink(true);
		const link = props.pendingRentalLinks.find((link) => link.rentalId === data.rentalId)!;
		const newPhone = `${data.phone.areaCode}${data.phone.numberWithoutCode}`;
		const smsDeliverySuccessful = await sendSMSPaylink(link);
		const emailDeliverySuccessful = await sendEmailPaylink(link);
		// needed to avoid duplicate delivery statuses
		debugger;
		await handleDeliveryStatusEventForEmail(link, smsDeliverySuccessful, emailDeliverySuccessful);
		await api().orders.doc(data.rentalId).update({
			'rentalLink.email': data.email,
			'rentalLink.phone': newPhone,
		});
		setIsOriginalPhoneSet(false);
		setIsOriginalPhoneSet(false);
		setNewLinkSent(true);
		setIsSendingPaylink(false);
		const confirmState = {
			rentalLink: {
				email: data.email,
				phone: data.phone.numberWithoutCode.length
					? data.phone.areaCode + data.phone.numberWithoutCode
					: null,
			} as RentalLinkInfo,
		};
		setOriginalEmail(data.email);
		setOriginalPhone(newPhone);

		push(Routes.NewRentalDone('link'), confirmState);
	};

	const renderSnackBar = (data: RentalLinkData) => {
		return (
			<Snackbar
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'center',
				}}
				open={props.rentalLinkSendingFailed}
				message=""
				action={[]}
			>
				<Box p={2} sx={{ maxWidth: '100vw', width: (theme) => theme.breakpoints.values.lg }}>
					<Banner
						sx={{
							width: '100%',
						}}
						variant="danger"
						raised
						title={
							originalPhone
								? t('failedLinkDialog.linkSendingNotSuccessfulSms', {
										phone: originalPhone,
										defaultValue: 'The rental link sent to {{phone}} was not delivered!',
								  })
								: t('failedLinkDialog.linkSendingNotSuccessfulEmail', {
										email: originalEmail,
										defaultValue: 'The rental link sent to {{email}} was not delivered!',
								  })
						}
						onClose={() => props.setRentalLinkSendingFailed(false)}
						description={
							<Stack
								width="100%"
								mt={2}
								direction={{ xs: 'column', md: 'row' }}
								alignItems={{ xs: 'stretch', md: 'center' }}
								justifyContent="space-between"
								spacing={4}
							>
								<Stack
									direction="row"
									alignItems="center"
									spacing={2}
									justifyContent="flex-start"
									flex={2}
								>
									<TextField
										id="email"
										variant="filled"
										placeholder={t('common:form.email')}
										value={data.email}
										onChange={(e) => handleEmailChange(e, data.rentalId)}
										margin="none"
										type="email"
										sx={{ flex: 1 }}
									/>
									<TextField
										id="phone"
										variant="filled"
										placeholder={t('common:form.phone')}
										value={data.phone.numberWithoutCode}
										onChange={(e) => handlePhoneChange(e, data.rentalId)}
										margin="none"
										type="tel"
										autoComplete="off"
										sx={{ flex: 1 }}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<TextField
														select
														variant="standard"
														value={`${data.phone.countryCode}:${data.phone.areaCode}`}
														onChange={(e) => handleAreaCodeChange(e, data.rentalId)}
														sx={{ width: 120 }}
														margin="none"
														SelectProps={{
															native: true,
														}}
														InputProps={{
															disableUnderline: true,
														}}
													>
														{countryCodes.map((countryCode) => (
															<option
																key={countryCode.code}
																value={countryCode.code + ':' + countryCode.dial_code}
															>
																{data.phone.areaCode === countryCode.dial_code
																	? `${countryCode.flag} ${countryCode.dial_code}`
																	: `${countryCode.flag} ${countryCode.name} (${countryCode.dial_code})`}
															</option>
														))}
													</TextField>
												</InputAdornment>
											),
										}}
									/>
								</Stack>
								<Stack
									direction="row"
									alignItems="center"
									justifyContent="flex-end"
									spacing={1}
									flex={1}
								>
									<Button
										key="openRental"
										variant="text"
										color="secondary"
										onClick={() => push(Routes.BookingPage(data.rentalId))}
									>
										{t('failedLinkDialog.openRental', 'Open rental')}
									</Button>
									<Button
										key="sendAgain"
										color="primary"
										variant="contained"
										disabled={isSendingPaylink}
										onClick={() => handleResendPaylink(data)}
									>
										{isSendingPaylink ? (
											<CircularProgress size={20} color="inherit" />
										) : (
											t('failedLinkDialog.sendAgain', 'Send again')
										)}
									</Button>
								</Stack>
							</Stack>
						}
					/>
				</Box>
			</Snackbar>
		);
	};

	return (
		(props.pendingRentalLinks.length &&
			props.pendingRentalLinks.some((link) => link.status === 'failed') &&
			renderSnackBar(props.pendingRentalLinks.find((link) => link.status === 'failed')!)) ||
		null
	);
};

const mapStateToProps = ({ view, storeRentals }: ReduxState): StateToProps => {
	const activeBookedRentals =
		storeRentals.activeBookedStoreRentals.kind === 'FETCHED'
			? storeRentals.activeBookedStoreRentals.data
			: [];
	return {
		rentalLinkSendingFailed: view.rentalLinkSendingFailed,
		pendingRentalLinks: view.pendingRentalLinks,
		activeBookedRentals,
	};
};
const dispatchProps: DispatchProps = {
	setRentalLinkSendingFailed,
	setPendingRentalLinks,
};

export default connect(mapStateToProps, dispatchProps)(SmsStatusSnackbar);
